import cx from 'classnames';
import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Collapse } from 'react-collapse';
import { Disclosure } from '@headlessui/react';

type Props = {
  className?: string;
  children: React.ReactNode | ((args: { isOpen: boolean; isVisible: boolean }) => JSX.Element);
  defaultOpen?: boolean;
};

type AnimatedCollapseCtx = {
  isOpen: boolean;
  isVisible: boolean;
  close: () => void;
  setVisible: (visible: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
  contentsRef: React.RefObject<HTMLDivElement>;
};

const AnimatedCollapseContext = createContext<AnimatedCollapseCtx | null>(null);

const useAnimatedCollapseContext = () => {
  const ctx = useContext(AnimatedCollapseContext);

  if (!ctx) {
    throw new Error(`You must wrap <Tooltip.Trigger> and <Tooltip.Content> in <Tooltip>!`);
  }

  return ctx;
};

export const AnimatedCollapse = ({ className, children, defaultOpen = false }: Props): JSX.Element => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const contentsRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(defaultOpen);

  const context = useCallback(
    (open: boolean, close: () => void) => ({
      isOpen: open,
      close,
      isVisible: visible,
      triggerRef,
      contentsRef,
      setVisible,
    }),
    [visible],
  );

  return (
    <section className={cx(className, 'text-sm sm:text-md')}>
      <Disclosure defaultOpen={defaultOpen}>
        {({ open, close }) => (
          <AnimatedCollapseContext.Provider value={context(open, close)}>
            {typeof children === 'function' ? children({ isOpen: open, isVisible: visible }) : children}
          </AnimatedCollapseContext.Provider>
        )}
      </Disclosure>
    </section>
  );
};

type TriggerProps = {
  className?: string;
  children: React.ReactNode | ((args: { isOpen: boolean; isVisible: boolean }) => JSX.Element);
};

AnimatedCollapse.Trigger = function AnimatedCollapseTrigger({ className, children }: TriggerProps) {
  const { isOpen, isVisible, triggerRef } = useAnimatedCollapseContext();

  return (
    <Disclosure.Button className={cx(className, 'w-full')} ref={triggerRef}>
      {typeof children === 'function' ? children({ isOpen, isVisible }) : children}
    </Disclosure.Button>
  );
};

type ContentsProps = {
  className?: string;
  children: React.ReactNode | ((args: { isOpen: boolean; isVisible: boolean }) => JSX.Element);
};

AnimatedCollapse.Contents = function AnimatedCollapseContents({ className, children }: ContentsProps) {
  const { isOpen, isVisible, setVisible, triggerRef, contentsRef } = useAnimatedCollapseContext();

  return (
    <Collapse
      isOpened={isOpen}
      theme={{
        collapse: 'transition-all duration-300',
        content: cx('transition-opacity', {
          'opacity-0': !isOpen,
          'duration-500': isOpen,
        }),
      }}
      onRest={({ isFullyClosed, isFullyOpened }) => {
        if (contentsRef.current) {
          if (isFullyClosed) {
            setVisible(false);
          }

          if (isFullyOpened && document.activeElement === triggerRef.current) {
            contentsRef.current.focus({ preventScroll: true });
          }
        }
      }}
      onWork={({ isOpened }) => {
        if (isOpened) {
          setVisible(true);
        }
      }}
    >
      <Disclosure.Panel
        static
        ref={contentsRef}
        role="region"
        tabIndex={-1}
        aria-hidden={!isOpen}
        className={cx(className, 'transition-none', {
          invisible: !isVisible,
        })}
      >
        {typeof children === 'function' ? children({ isOpen, isVisible }) : children}
      </Disclosure.Panel>
    </Collapse>
  );
};
